import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ConversationDetail } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useConvertConversation } from '../hooks/use-conversation-mutations';
import {
  convertTicketDefaults,
  convertTicketFormSchema,
  knownEmailOf,
  TICKET_PRIORITIES,
  toConvertConversation,
  type ConvertTicketForm,
} from '../schemas/convert-ticket-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['subject', 'priority', 'email']);

function ConvertTicketBody({
  conversation,
  onClose,
}: {
  conversation: ConversationDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation(['conversations', 'common']);
  const message = useErrorMessage();
  const convert = useConvertConversation(conversation.storeId, conversation.id);
  const knownEmail = knownEmailOf(conversation);
  const form = useForm<ConvertTicketForm>({
    resolver: useI18nResolver(convertTicketFormSchema),
    defaultValues: convertTicketDefaults(conversation),
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await convert.mutateAsync(toConvertConversation(values));
      onClose();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const requestId = error instanceof ApiError ? error.requestId : undefined;
      form.setError('root.server', {
        type: 'server',
        message: requestId
          ? `${message(error)} (${t('errors:requestIdShort', { requestId })})`
          : message(error),
      });
    }
  });

  const serverError = form.formState.errors.root?.server?.message;

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {serverError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('conversations:convert.subject')}</FormLabel>
              <FormControl>
                <Input autoFocus maxLength={200} {...field} />
              </FormControl>
              <FormDescription>{t('conversations:convert.subjectHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('conversations:header.priority')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full" onBlur={field.onBlur}>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {TICKET_PRIORITIES.map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {t(`conversations:priority.${priority}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {knownEmail ? (
          <p className="text-muted-foreground text-sm">
            {t('conversations:convert.emailKnown', { email: knownEmail })}
          </p>
        ) : (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('conversations:convert.email')}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="off" maxLength={320} {...field} />
                </FormControl>
                <FormDescription>{t('conversations:convert.emailHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={convert.isPending}>
            {convert.isPending
              ? t('conversations:convert.submitting')
              : t('conversations:convert.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * Chat or offline message → ticket, in place: same number and thread, targets from the store's
 * SLA settings dated from the start, and from then on every reply is emailed (LC9).
 */
export function ConvertTicketDialog({
  conversation,
  open,
  onOpenChange,
}: {
  conversation: ConversationDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('conversations');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('convert.title', { number: conversation.number })}</DialogTitle>
          <DialogDescription>{t('convert.description')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <ConvertTicketBody conversation={conversation} onClose={() => onOpenChange(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
